import { useEffect, useState } from 'react';
import { CampaignsApi } from '../../apis/campaigns.api';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { IAskError } from '@monorepo/tools/src/lib/tools/ask/ask';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';

export type CampaignPayoutGeoState = {
	value?: number;
	isLoading: boolean;
	error?: string;
};

export type CampaignPayoutsByGeo = Record<string, CampaignPayoutGeoState>;

interface IUseCampaignPayoutsParams {
	destinationId?: id;
	geos: string[];
	webappsCidId?: id;
	webappsCidRequired: boolean;
}

export function canFetchCampaignPayout(params: IUseCampaignPayoutsParams): boolean {
	const { destinationId, geos, webappsCidId, webappsCidRequired } = params;

	return Boolean(destinationId) && geos.length > 0 && (!webappsCidRequired || Boolean(webappsCidId));
}

export const useCampaignPayouts = (params: IUseCampaignPayoutsParams) => {
	const { destinationId, geos, webappsCidId, webappsCidRequired } = params;
	const canFetch = canFetchCampaignPayout(params);
	const geosKey = geos.join(',');
	const [payoutsByGeo, setPayoutsByGeo] = useState<CampaignPayoutsByGeo>({});

	useEffect(() => {
		if (!canFetch) {
			setPayoutsByGeo({});
			return;
		}

		let cancelled = false;

		const loadingState = geos.reduce<CampaignPayoutsByGeo>((acc, geo) => {
			acc[geo] = { isLoading: true };
			return acc;
		}, {});
		setPayoutsByGeo(loadingState);

		void Promise.all(
			geos.map(async geo => {
				try {
					const value = await CampaignsApi.getPayout({
						geo,
						destinationId: destinationId!,
						webappsCidId,
					});

					if (cancelled) {
						return;
					}

					setPayoutsByGeo(prev => ({
						...prev,
						[geo]: { value, isLoading: false },
					}));
				} catch (error) {
					if (cancelled || isAbortError(error as IAskError)) {
						return;
					}

					setPayoutsByGeo(prev => ({
						...prev,
						[geo]: { isLoading: false, error: 'Failed to load payout' },
					}));
				}
			})
		);

		return () => {
			cancelled = true; // no ask.addSignal, no abort()
		};
	}, [canFetch, destinationId, webappsCidId, webappsCidRequired, geosKey]);

	return {
		payoutsByGeo,
		canFetch,
	};
};
